import { Badge, Button, Space, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { GroupMonitoringGroup, GroupMonitoringSortField } from '@models/group-monitoring';
import { GroupStatus } from '@models/grouping-agent';

/** 보충 필요(short) 그룹 수 — 0이면 `-`, 1 이상만 빨간 태그 */
export const renderShortGroupCount = (n?: number | null) => (n != null && n > 0 ? <Tag color="red">{n}</Tag> : '-');

export const formatShortGroupCountStat = (n?: number | null) => (n != null && n > 0 ? n : '-');

export const sortByShortGroupCount = <T extends { shortGroupCount?: number }>(a: T, b: T) =>
  (a.shortGroupCount ?? 0) - (b.shortGroupCount ?? 0);

export const shortGroupCountColumn = <T extends { shortGroupCount?: number }>(
  width = 72,
  serverSort?: { field?: GroupMonitoringSortField; order?: SortOrder },
): ColumnsType<T>[0] => ({
  title: '보충',
  dataIndex: 'shortGroupCount',
  width,
  align: 'center',
  render: renderShortGroupCount,
  sorter: serverSort ? true : sortByShortGroupCount,
  sortOrder: serverSort?.field === 'shortGroupCount' ? serverSort.order : undefined,
});

export const partnerNameColumn = <T extends { partnerName?: string }>(): ColumnsType<T>[0] => ({
  title: '파트너명',
  dataIndex: 'partnerName',
  ellipsis: true,
  width: 120,
  render: (v?: string) => v || '-',
  sorter: (a, b) => (a.partnerName || '').localeCompare(b.partnerName || ''),
});

export const productNameColumn = <T extends { productName?: string }>(): ColumnsType<T>[0] => ({
  title: '상품명',
  dataIndex: 'productName',
  ellipsis: true,
  width: 200,
  render: (v?: string) => v || '-',
  sorter: (a, b) => (a.productName || '').localeCompare(b.productName || ''),
});

export const kakaoColumn = (serverSort?: {
  field?: GroupMonitoringSortField;
  order?: SortOrder;
}): ColumnsType<GroupMonitoringGroup>[0] => ({
  title: '카카오',
  dataIndex: 'kakaoEnrollmentCount',
  width: 72,
  align: 'center',
  sorter: serverSort ? true : (a, b) => (a.kakaoEnrollmentCount ?? 0) - (b.kakaoEnrollmentCount ?? 0),
  sortOrder: serverSort?.field === 'kakaoEnrollmentCount' ? serverSort.order : undefined,
  render: (_, r) => ((r.kakaoEnrollmentCount ?? 0) > 0 ? <Tag color="yellow">{r.kakaoEnrollmentCount}</Tag> : '-'),
});

export const withListServerSort = <T,>(
  field: GroupMonitoringSortField,
  column: ColumnsType<T>[0],
  serverSort?: { field?: GroupMonitoringSortField; order?: SortOrder },
): ColumnsType<T>[0] => {
  if (!serverSort) return column;
  return {
    ...column,
    sorter: true,
    sortOrder: serverSort.field === field ? serverSort.order : undefined,
  };
};

export const buildGroupMonitoringColumns = (opts: {
  onOpenDetail: (group: GroupMonitoringGroup) => void;
  serverSort?: { field?: GroupMonitoringSortField; order?: SortOrder };
}): ColumnsType<GroupMonitoringGroup> => {
  const serverSort = opts.serverSort;
  return [
    withListServerSort('partnerName', partnerNameColumn<GroupMonitoringGroup>(), serverSort),
    withListServerSort('productName', productNameColumn<GroupMonitoringGroup>(), serverSort),
    { title: '회차명', dataIndex: 'optionName', ellipsis: true, width: 180, render: (v?: string) => v || '-' },
    withListServerSort(
      'startDate',
      {
        title: '수업일',
        dataIndex: 'startDate',
        width: 150,
        render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
        sorter: (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
      },
      serverSort,
    ),
    { title: '그룹명', dataIndex: 'name', width: 100 },
    {
      title: '연령',
      dataIndex: 'memberAgeLabels',
      width: 160,
      ellipsis: { showTitle: false },
      render: (_, r) => {
        const v = r.memberAgeLabels;
        if (!v || v === '-') return '-';
        return <Tooltip title={r.memberAges?.length ? r.memberAges.join(' · ') : v}>{v}</Tooltip>;
      },
    },
    withListServerSort(
      'ageGap',
      {
        title: '나이 편차',
        dataIndex: 'ageGap',
        width: 100,
        align: 'center',
        render: (_, r) => {
          if ((r.ageKnownCount ?? 0) < 2) return '-';
          const label =
            r.ageMin != null && r.ageMax != null ? `${r.ageGap}세 (${r.ageMin}~${r.ageMax})` : `${r.ageGap ?? 0}세`;
          return r.hasWideAgeGap ? <Tag color="orange">{label}</Tag> : label;
        },
      },
      serverSort,
    ),
    {
      title: '함께신청',
      dataIndex: 'memberFriendLabels',
      width: 170,
      ellipsis: { showTitle: false },
      render: (_, r) => {
        const v = r.memberFriendLabels;
        if (!v || v === '-') return '-';
        return <Tooltip title={r.memberFriends?.length ? r.memberFriends.join(' · ') : v}>{v}</Tooltip>;
      },
    },
    kakaoColumn(serverSort),
    withListServerSort(
      'studentsMin',
      {
        title: '최소인원',
        dataIndex: 'studentsMin',
        width: 80,
        align: 'center',
        sorter: (a, b) => a.studentsMin - b.studentsMin,
      },
      serverSort,
    ),
    withListServerSort(
      'studentsMax',
      {
        title: '최대인원',
        dataIndex: 'studentsMax',
        width: 80,
        align: 'center',
        render: (v: number) => (v < 0 ? '제한없음' : v),
        sorter: (a, b) => a.studentsMax - b.studentsMax,
      },
      serverSort,
    ),
    withListServerSort(
      'currentCount',
      {
        title: '현재인원',
        dataIndex: 'currentCount',
        width: 80,
        align: 'center',
        sorter: (a, b) => a.currentCount - b.currentCount,
      },
      serverSort,
    ),
    {
      title: '상태',
      dataIndex: 'groupStatus',
      width: 80,
      render: (s: GroupStatus) => (
        <Tag color={s === 'complete' ? 'green' : 'orange'}>{s === 'complete' ? '완료' : '보충'}</Tag>
      ),
    },
    withListServerSort(
      'minNeeded',
      {
        title: '보충 필요',
        dataIndex: 'minNeeded',
        width: 100,
        align: 'center',
        render: (n: number) => (n > 0 ? <Badge count={n} color="red" /> : '-'),
        sorter: (a, b) => (a.minNeeded ?? 0) - (b.minNeeded ?? 0),
      },
      serverSort,
    ),
    {
      title: '표시',
      width: 180,
      render: (_, r) => (
        <Space size={4} wrap>
          {(r.namePendingCount ?? 0) > 0 && <Tag color="orange">이름 보충 {r.namePendingCount}명</Tag>}
          {r.needsRecruitment && (r.minNeeded ?? 0) > 0 && <Tag color="volcano">라스트찬스 +{r.minNeeded}명</Tag>}
        </Space>
      ),
    },
    {
      title: '에이전트',
      width: 100,
      render: (_, r) =>
        r.latestAgentRunId ? (
          <Tooltip title={r.latestAgentRunId}>
            <Button type="link" size="small" href={`/grouping-agent-runs/${r.latestAgentRunId}`}>
              Run
            </Button>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: '상세',
      width: 80,
      fixed: 'right',
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => opts.onOpenDetail(r)}>
          상세
        </Button>
      ),
    },
  ];
};
